import React from 'react';
import { Form, Input, Select } from 'antd';
import type { Rule } from 'antd/es/form';

const { TextArea } = Input;
const { Option } = Select;

interface FormInputProps {
  name: string;
  label: string;
  placeholder?: string;
  rules?: Rule[];
  required?: boolean;
  className?: string;
  disabled?: boolean;
  type?: 'text' | 'password' | 'email' | 'number';
}

interface FormTextAreaProps extends Omit<FormInputProps, 'type'> {
  rows?: number;
  maxLength?: number;
  showCount?: boolean;
}

interface SelectOption {
  value: string | number;
  label: string;
}

interface FormSelectProps extends Omit<FormInputProps, 'type' | 'placeholder'> {
  options: SelectOption[];
  placeholder?: string;
  onChange?: (value: string) => void;
  mode?: 'multiple' | 'tags';
  allowClear?: boolean;
  defaultValue?: string;
}

/**
 * Common form input field with label and validation
 *
 * @param name - Form field name
 * @param label - Label text
 * @param placeholder - Input placeholder
 * @param rules - Validation rules (if not provided and required=true, uses default required rule)
 * @param required - Whether field is required (default: false)
 * @param className - Additional CSS classes
 * @param disabled - Whether input is disabled
 * @param type - Input type (text, password, email, number)
 *
 * @example
 * <FormInput
 *   name="email"
 *   label="Email Address"
 *   placeholder="Enter your email"
 *   required
 *   type="email"
 * />
 */
export const FormInput: React.FC<FormInputProps> = ({
  name,
  label,
  placeholder,
  rules,
  required = false,
  className = '',
  disabled = false,
  type = 'text',
}) => {
  const defaultRules: Rule[] = required
    ? [{ required: true, whitespace: true, message: `Please enter ${label.toLowerCase()}` }]
    : [];

  return (
    <Form.Item
      label={label}
      name={name}
      rules={rules || defaultRules}
      className={className}
    >
      <Input
        placeholder={placeholder || label}
        disabled={disabled}
        type={type}
      />
    </Form.Item>
  );
};

/**
 * Common form textarea field with label and validation
 *
 * @param name - Form field name
 * @param label - Label text
 * @param placeholder - Input placeholder
 * @param rules - Validation rules
 * @param required - Whether field is required
 * @param rows - Number of textarea rows (default: 4)
 * @param maxLength - Maximum character length
 * @param showCount - Show character count
 *
 * @example
 * <FormTextArea
 *   name="description"
 *   label="Description"
 *   placeholder="Enter description"
 *   rows={6}
 *   maxLength={500}
 *   showCount
 * />
 */
export const FormTextArea: React.FC<FormTextAreaProps> = ({
  name,
  label,
  placeholder,
  rules,
  required = false,
  className = '',
  disabled = false,
  rows = 4,
  maxLength,
  showCount = false,
}) => {
  const defaultRules: Rule[] = required
    ? [{ required: true, whitespace: true, message: `Please enter ${label.toLowerCase()}` }]
    : [];

  return (
    <Form.Item
      label={label}
      name={name}
      rules={rules || defaultRules}
      className={className}
    >
      <TextArea
        placeholder={placeholder || label}
        disabled={disabled}
        rows={rows}
        maxLength={maxLength}
        showCount={showCount}
      />
    </Form.Item>
  );
};

/**
 * Common form select field with label and validation
 *
 * @param name - Form field name
 * @param label - Label text
 * @param options - Array of { value, label } options
 * @param placeholder - Select placeholder
 * @param rules - Validation rules
 * @param required - Whether field is required
 * @param onChange - Callback when selection changes
 * @param mode - 'multiple' for multi-select, 'tags' for tag input
 * @param allowClear - Show clear button
 * @param defaultValue - Default selected value
 *
 * @example
 * <FormSelect
 *   name="sportId"
 *   label="Sport"
 *   options={[
 *     { value: '1', label: 'AFL' },
 *     { value: '2', label: 'NRL' }
 *   ]}
 *   placeholder="Select a sport"
 *   required
 * />
 */
export const FormSelect: React.FC<FormSelectProps> = ({
  name,
  label,
  options,
  placeholder,
  rules,
  required = false,
  className = '',
  disabled = false,
  onChange,
  mode,
  allowClear = false,
  defaultValue,
}) => {
  const defaultRules: Rule[] = required
    ? [{ required: true, message: `Please select ${label.toLowerCase()}` }]
    : [];

  return (
    <Form.Item
      label={label}
      name={name}
      rules={rules || defaultRules}
      className={className}
    >
      <Select
        placeholder={placeholder || `Select ${label.toLowerCase()}`}
        disabled={disabled}
        onChange={onChange}
        mode={mode}
        allowClear={allowClear}
        defaultValue={defaultValue}
      >
        {options.map((option) => (
          <Option key={option.value} value={option.value}>
            {option.label}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
};

const FormComponents = { FormInput, FormTextArea, FormSelect };
export default FormComponents;
